<template>
    <div>
        <div
            class="stone-recommendations"
            data-qa="stone-recommendations"
        >
            <Tabs
                :current-tab-resolver
                :tabs
                page-type="system-page"
            />

            <div
                v-if="
                    stoneRecommendations?.__typename ===
                    'StoneRecommendationsResponseSuccess'
                "
                class="recommendations-wrapper"
            >
                <div
                    v-qa="'budget-slider'"
                    class="budget-slider"
                >
                    <div class="toggle-container">
                        <span class="label">
                            {{ t('stone_recommendations.total_budget') }}

                            <LazyTooltip
                                inline
                                page-type="system-page"
                                path="/stone-recommendations/total-budget"
                            />

                            <span
                                v-if="budgetFilterForUi"
                                class="budget-range"
                            >
                                {{ formatter(budgetFilterForUi.min, 0) }}—{{
                                    formatter(budgetFilterForUi.max, 0)
                                }}
                            </span>
                        </span>

                        <button
                            class="slider-toggle"
                            @click="toggleRangeSlider"
                        >
                            Change
                        </button>
                    </div>

                    <div
                        v-if="boundsInCurrency"
                        :class="classRangeSlider"
                        class="range-slider"
                    >
                        <InputRangeTwoKnobbedExponentialOk
                            v-if="budgetFilterForUi"
                            v-model="budgetFilterForUi"
                            :bounds="{
                                min: parseFloat(boundsInCurrency.min.amount),
                                max: parseFloat(boundsInCurrency.max.amount),
                            }"
                            :debounce="500"
                            :linear-percent="90"
                            :max-linear="5_000"
                            :steps="10_000"
                            :value-formatter="formatter"
                        />
                    </div>
                </div>

                <div
                    v-if="recommendations"
                    ref="container"
                    :class="classRecommendationsCards"
                    :data-qa-loading="loading"
                    class="recommendation-cards"
                >
                    <div
                        v-for="(item, index) in recommendations"
                        :key="index"
                        ref="elements"
                        class="card"
                    >
                        <EngagementRingStoneRecommendationCard
                            :content="item.content"
                            :data="item.data"
                            :highlighted="item.highlighted"
                            page-type="system-page"
                            @select="item.onSelect"
                        />
                    </div>

                    <div class="card mobile-extra">
                        <div class="inner">
                            <div class="description">
                                {{
                                    t(
                                        'stone_recommendations.browse_more_card.description'
                                    )
                                }}
                            </div>

                            <ThButton
                                :to="expertSearchUrl"
                                color="var(--blue)"
                                size="small"
                                type="solid"
                                @click="trackOwnStoneSearch"
                            >
                                {{
                                    t(
                                        'stone_recommendations.browse_more_card.button'
                                    )
                                }}
                            </ThButton>
                        </div>
                    </div>
                </div>

                <ButtonRound
                    :aria-label="t('catalogue.carousel.button_prev')"
                    :disabled="isAtStart"
                    class="button-round left"
                    icon="angle-left"
                    @click="previous"
                />

                <ButtonRound
                    :aria-label="t('catalogue.carousel.button_next')"
                    :disabled="isAtEnd"
                    class="button-round right"
                    icon="angle-right"
                    @click="next"
                />
            </div>

            <LoadingAnimation
                v-else-if="fetching || stale"
                class="loading-animation"
            />

            <div
                v-if="
                    !stoneRecommendations ||
                    stoneRecommendations?.__typename ===
                        'StoneRecommendationsResponseFailure'
                "
            >
                <div class="stone-recommendations-empty-state">
                    <ThSpacer size="LARGE" />

                    <ThTitle
                        alignment="CENTER"
                        color="var(--blue)"
                        size="SMALL"
                        tag="H3"
                    >
                        {{ t('stone_recommendations.ring_builder_empty') }}
                    </ThTitle>

                    <ThSpacer size="MEDIUM" />

                    <ThButton
                        :to="routes.engagementRings({})"
                        color="var(--blue)"
                        size="medium"
                        type="solid"
                    >
                        {{
                            t(
                                'stone_recommendations.ring_builder_empty_button_text'
                            )
                        }}
                    </ThButton>

                    <ThSpacer size="LARGE" />
                </div>
            </div>

            <PageErrorFiveHundred
                v-else-if="responseErrors.length"
                :errors="responseErrors"
            >
                <ThButton
                    :disabled="fetching"
                    @click="executeQuery"
                >
                    {{ t('globals.try_again') }}
                </ThButton>
            </PageErrorFiveHundred>

            <div
                v-if="stoneRecommendationsData?.cms?.fullSearchButtonText"
                class="all-results-cta"
            >
                <ThButton
                    v-qa="'choose-your-own'"
                    :to="expertSearchUrl"
                    color="var(--blue)"
                    type="bordered"
                    @click="trackOwnStoneSearch"
                >
                    {{ stoneRecommendationsData?.cms?.fullSearchButtonText }}
                </ThButton>
            </div>
        </div>

        <CmsBlocks
            v-if="blocks"
            :data="blocks"
            page-type="system-page"
        />
    </div>
</template>

<script lang="ts">
import type { StoneRecommendationGemsApiGemstoneFragment } from '@/graphql/api'
import { type StoneColorCode } from '@/graphql/api'
import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'

const colorPriorities: StoneColorCode[] = [
    'GEMSTONE_BLUE',
    'GEMSTONE_GREEN',
    'GEMSTONE_TEAL',
    'GEMSTONE_PEACH',
    'GEMSTONE_PINK',
    'GEMSTONE_PURPLE',
    'GEMSTONE_YELLOW',
]

const sortAndTrimGemstones = <
    TStones extends {
        color: {
            systemCode: StoneRecommendationGemsApiGemstoneFragment['color']['systemCode']
        }
        stone: {
            __typename: StoneRecommendationGemsApiGemstoneFragment['stone']['__typename']
        }
    },
>(
    gemstones: TStones[]
): TStones[] =>
    gemstones
        .sort((a, b) => {
            let aIndex = colorPriorities.indexOf(a.color.systemCode)
            let bIndex = colorPriorities.indexOf(b.color.systemCode)

            if (aIndex === -1) {
                aIndex = 999
            }

            if (bIndex === -1) {
                bIndex = 999
            }

            if (a.stone.__typename == 'StoneRecommendationNotFound') {
                aIndex = 999
            }

            if (b.stone.__typename == 'StoneRecommendationNotFound') {
                bIndex = 999
            }

            return aIndex - bIndex
        })
        .slice(0, 3)

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    type SortType = {
        color: { systemCode: StoneColorCode }
        stone: {
            __typename: StoneRecommendationGemsApiGemstoneFragment['stone']['__typename']
        }
    }

    const foundStone = {
        __typename: 'GemsApi_Stone',
    } as const

    const notFoundStone = {
        __typename: 'StoneRecommendationNotFound',
    } as const

    const makeStone = (
        color: StoneColorCode,
        foundOrNotFound: SortType['stone']
    ): SortType => {
        return {
            color: {
                systemCode: color,
            },
            stone: foundOrNotFound,
        }
    }

    describe('Stone Recommendations', () => {
        test('Sorted Gemstones, first prio not found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', foundStone)
            const pink = makeStone('GEMSTONE_PINK', notFoundStone)
            const purple = makeStone('GEMSTONE_PURPLE', foundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

            const sorted = sortAndTrimGemstones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(peach)
            expect(sorted[1]).toBe(purple)
            expect(sorted[2]).toBe(blue)
        })

        test('Sorted Gemstones, one found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', foundStone)
            const pink = makeStone('GEMSTONE_PINK', notFoundStone)
            const purple = makeStone('GEMSTONE_PURPLE', notFoundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

            const sorted = sortAndTrimGemstones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(peach)
            expect(sorted[1]).toBe(blue)
            expect(sorted[2]).toBe(green)
        })

        test('Sorted Gemstones, nothing found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', notFoundStone)
            const pink = makeStone('GEMSTONE_PINK', notFoundStone)
            const purple = makeStone('GEMSTONE_PURPLE', notFoundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', notFoundStone)

            const sorted = sortAndTrimGemstones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(blue)
            expect(sorted[1]).toBe(green)
            expect(sorted[2]).toBe(teal)
        })

        test('Sorted Gemstones, last 3 found', () => {
            const blue = makeStone('GEMSTONE_BLUE', notFoundStone)
            const green = makeStone('GEMSTONE_GREEN', notFoundStone)
            const teal = makeStone('GEMSTONE_TEAL', notFoundStone)
            const peach = makeStone('GEMSTONE_PEACH', notFoundStone)
            const pink = makeStone('GEMSTONE_PINK', foundStone)
            const purple = makeStone('GEMSTONE_PURPLE', foundStone)
            const yellow = makeStone('GEMSTONE_YELLOW', foundStone)

            const sorted = sortAndTrimGemstones([
                blue,
                green,
                teal,
                peach,
                pink,
                purple,
                yellow,
            ])

            expect(sorted).length(3)
            expect(sorted[0]).toBe(pink)
            expect(sorted[1]).toBe(purple)
            expect(sorted[2]).toBe(yellow)
        })
    })
}
</script>

<script lang="ts" setup>
import { computed, onMounted, ref, toRef, watch } from 'vue'
import type { RouteLocationNamedRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { Bounds } from 'wucdbm-exponential-slider'

import { useCountryPlugin } from '@/plugins/country.ts'
import type { RingBuilderStoneTypes } from '@/domain/product/ring/ring-builder'
import { useRoutes } from '@/router/routes'
import { assertNever, includes } from '@/helpers'
import { useTranslator } from '@/i18n'
import {
    type EngagementRingStoneRecommendationCardFragment,
    GemsApi_StoneCategory,
    GemsApi_StoneShape,
    type RingBuilderStoneRecommendationsFragment,
    StoneCategoryCode,
    type StoneRecommendationsFragment,
    type StoneRecommendationsNotFoundFragment,
    type StoneRecommendationsQueryVariables,
    type StoneShapeCode,
} from '@/graphql/api'
import { useStoneRecommendationsQuery } from '@/views/product/engagement-ring/stone-recommendations/StoneRecommendations.generated.ts'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'
import { useResponseError } from '@/helpers/errors'
import {
    type RecommendationsAnalyticsOption,
    useRecommendationsAnalytics,
} from '../../composables/analytics/useRecommendationsAnalytics'
import { useCurrencyInput } from '@/plugins/currency-input'
import { useMoneyFormatter } from '@/plugins/money-formatter'
import type { InputRangeTwoKnobbedValueFormatter } from '@/components/ui/inputs/range/input-range-two-knobbed-props'
import { useRingBuilder } from '@/plugins/ring-builder.ts'

import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'
import EngagementRingStoneRecommendationCard from '@/views/product/engagement-ring/stone-recommendations/EngagementRingStoneRecommendationCard.vue'
import CmsBlocks from '@/components/cms/CmsBlocks.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import Tabs from '@/components/layout/Tabs.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'

const props = defineProps<{
    category: GemsApi_StoneCategory
}>()

const { countryCode } = useCountryPlugin()
const { locale } = useTranslator()
const { t } = useI18n()

const routes = useRoutes()
const { ring, fetchingRingBuilderSetMutation, addStone } = useRingBuilder()

const ringGemsShapeCode = computed((): GemsApi_StoneShape | undefined => {
    const shape = ring.value?.centreStoneConstraints.shape.systemCode

    if (includes(Object.values(GemsApi_StoneShape), shape)) {
        return shape
    }

    return undefined
})

const ringStoneShapeCode = computed((): StoneShapeCode | undefined => {
    return ring.value?.centreStoneConstraints.shape.systemCode
})

const weight = computed(() => ({
    min: Number(ring.value?.centreStoneConstraints.weight.min) || null,
    max: Number(ring.value?.centreStoneConstraints.weight.max) || null,
}))

const expertSearchUrl = computed((): RouteLocationNamedRaw => {
    switch (props.category) {
        case GemsApi_StoneCategory.Diamond:
            return routes.diamondSearch(
                props.category,
                ringGemsShapeCode.value,
                {
                    weight: weight.value,
                }
            )
        case GemsApi_StoneCategory.LabGrownDiamond:
            return routes.nonSku.labGrownDiamondSearch({
                shape: ringStoneShapeCode.value,
                caratWeightRange: {
                    ...ring.value?.centreStoneConstraints.weight,
                },
            })
        case GemsApi_StoneCategory.FancyDiamond:
        // fallthrough
        case GemsApi_StoneCategory.LabGrownFancyDiamond:
            return routes.fancyDiamondSearch(
                props.category,
                undefined,
                ringGemsShapeCode.value,
                {
                    weight: weight.value,
                }
            )
        case GemsApi_StoneCategory.Gemstone:
            return routes.gemstoneSearch({
                shape: ringStoneShapeCode.value && [ringStoneShapeCode.value],
                weight: weight.value,
            })
        case GemsApi_StoneCategory.LabGrownGemstone:
            return {}
        default:
            return assertNever(props.category, {})
    }
})

const currency = useCurrencyInput()
const moneyFormatter = useMoneyFormatter()

const budgetFilter = ref<Bounds | null>(null)

onMounted(() => {
    budgetFilter.value = null
})

const budgetFilterForUi = computed<Bounds | null | undefined, Bounds | null>({
    get: () => {
        return budgetFilter.value ?? priceInitialValues.value
    },
    set: (value) => {
        budgetFilter.value = value
    },
})

const variables = computed((): StoneRecommendationsQueryVariables => {
    const bf = budgetFilter.value

    return {
        hasPrice: !!bf,
        minPrice: bf && {
            amount: bf.min.toString(),
            currency: currency.value,
        },
        maxPrice: bf && {
            amount: bf.max.toString(),
            currency: currency.value,
        },
        locale: locale.value,
        currency: currency.value,
        country: countryCode.value!,
    }
})

const pause = ref(false)

const stoneRecommendationsResponse = useStoneRecommendationsQuery({
    variables,
    pause,
})

await stoneRecommendationsResponse

const {
    stale,
    fetching,
    data: stoneRecommendationsData,
    executeQuery,
} = stoneRecommendationsResponse

const responseErrors = useResponseError(stoneRecommendationsResponse)

const gemstoneCmsData = computed(() => {
    return Object.fromEntries(
        stoneRecommendationsData.value?.cms?.nonSkuGemStones.map((item) => {
            return [item.color, item.content]
        }) ?? []
    )
})

const getRecommendations = (
    input: RingBuilderStoneRecommendationsFragment | undefined | null
): StoneRecommendationsFragment | null => {
    if (input?.__typename === 'RingBuilderMountFragment') {
        return input.stoneRecommendations
    }

    return null
}

const stoneRecommendations = toRef(
    () =>
        getRecommendations(
            stoneRecommendationsData.value?.ringBuilder.position1
        ) ||
        getRecommendations(
            stoneRecommendationsData.value?.ringBuilder.position2
        )
)

const recommendations = computed<
    {
        data: RingBuilderStoneTypes | StoneRecommendationsNotFoundFragment
        content: EngagementRingStoneRecommendationCardFragment | undefined
        highlighted?: boolean
        onSelect: (stone: RingBuilderStoneTypes) => void
    }[]
>(() => {
    if (!stoneRecommendations.value) {
        return []
    }

    if (!stoneRecommendationsData.value) {
        return []
    }

    if (
        stoneRecommendations.value?.__typename !==
        'StoneRecommendationsResponseSuccess'
    ) {
        return []
    }

    switch (props.category) {
        case GemsApi_StoneCategory.Diamond:
            return [
                {
                    content:
                        stoneRecommendationsData.value.cms?.naturalDiamonds
                            .forSizeCard,
                    data: stoneRecommendations.value.naturalDiamondBestForSize,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for size'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.naturalDiamonds
                            .forBalanceCard,
                    data: stoneRecommendations.value
                        .naturalDiamondBestForBalance,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for balance'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.naturalDiamonds
                            .labGrownAlternative,
                    data: stoneRecommendations.value.nonSkuLabGrownBestForPrice,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for price'),
                    highlighted: true,
                },
            ]

        case GemsApi_StoneCategory.LabGrownDiamond:
            return [
                {
                    content:
                        stoneRecommendationsData.value.cms?.nonSkuLabGrown
                            .forPriceCard,
                    data: stoneRecommendations.value.nonSkuLabGrownBestForPrice,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for price'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.nonSkuLabGrown
                            .forSizeCard,
                    data: stoneRecommendations.value.nonSkuLabGrownBestForSize,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for size'),
                },
                {
                    content:
                        stoneRecommendationsData.value.cms?.nonSkuLabGrown
                            .naturalDiamondAlternative,
                    data: stoneRecommendations.value.naturalDiamondBestForSize,
                    onSelect: (stone: RingBuilderStoneTypes) =>
                        selectStone(stone, 'Best option for balance'),
                    highlighted: true,
                },
            ]

        case GemsApi_StoneCategory.Gemstone:
            return sortAndTrimGemstones([
                ...stoneRecommendations.value.skuGemStones,
            ]).map((nonSkuGemstone) => ({
                content: gemstoneCmsData.value[nonSkuGemstone.color.systemCode],
                data: nonSkuGemstone.stone,
                onSelect: (stone: RingBuilderStoneTypes) =>
                    selectStone(stone, 'Gemstone option'),
            }))

        default:
            return []
    }
})

const { previous, next, container, elements, isAtStart, isAtEnd, current } =
    useCarousel(computed(() => recommendations.value.length + 1))

watch(
    () => props.category,
    () => (current.value = 0)
)

const priceInitialValues = computed(() => {
    const v = stoneRecommendations.value
    if (
        v?.__typename === 'StoneRecommendationsResponseSuccess' &&
        v.priceInitialValues
    ) {
        return {
            min: parseFloat(v.priceInitialValues.min.amount),
            max: parseFloat(v.priceInitialValues.max.amount),
        }
    }
    return undefined
})

const tabs = [
    {
        name: GemsApi_StoneCategory.LabGrownDiamond,
        label: t('stone_recommendations.gemType.lab_grown'),
        labelExtra: t('stone_recommendations.gemCategory.diamonds'),
        to: routes.ringBuilder.recommendations('lab-grown-diamonds'),
        tooltipPath: 'diamond-search/tabs/lab-grown-diamonds',
    },
    {
        name: GemsApi_StoneCategory.Diamond,
        label: t('stone_recommendations.gemType.natural'),
        labelExtra: t('stone_recommendations.gemCategory.diamonds'),
        to: routes.ringBuilder.recommendations('natural-diamonds'),
        tooltipPath: 'diamond-search/tabs/natural-diamonds',
    },
    {
        name: StoneCategoryCode.Gemstone,
        label: t('stone_recommendations.gemCategory.gemstones'),
        to: routes.ringBuilder.recommendations('gemstones'),
        tooltipPath: 'diamond-search/tabs/gemstones',
    },
]

const currentTabResolver = (tab: Pick<(typeof tabs)[number], 'name'>) =>
    tab.name === props.category

const blocks = toRef(() => stoneRecommendationsData.value?.cms?.blocks)

const boundsInCurrency = computed(() => {
    const r = stoneRecommendations.value

    if (r?.__typename === 'StoneRecommendationsResponseSuccess') {
        return r.priceBounds
    }

    return undefined
})

const formatter: InputRangeTwoKnobbedValueFormatter = (value) =>
    moneyFormatter({
        amount: value.toString(),
        currency: boundsInCurrency.value?.min.currency || 'GBP',
    })

const { routerPush } = useRouterNavigation()

const { trackStoneSelected, trackOwnStoneSearch } = useRecommendationsAnalytics(
    toRef(() => props.category),
    budgetFilter
)

const selectStone = (
    stone: RingBuilderStoneTypes,
    option: RecommendationsAnalyticsOption
) => {
    const r = ring.value

    if (r) {
        addStone(stone).then(() => {
            trackStoneSelected(stone, option)

            if (ring.value) {
                routerPush(routes.ringBuilder.ring(ring.value, stone))
            }
        })
    } else {
        routerPush(routes.engagementRings({}))
    }
}

const loading = computed(
    () => fetching.value || stale.value || fetchingRingBuilderSetMutation.value
)

const classRecommendationsCards = computed(() => ({
    'fetching': loading.value,
}))

const isSliderMobileVisible = ref(false)
const toggleRangeSlider = () => {
    isSliderMobileVisible.value = !isSliderMobileVisible.value
}
const classRangeSlider = computed(() => ({
    'visible': isSliderMobileVisible.value,
}))
</script>

<style lang="scss" scoped>
.stone-recommendations {
    padding-top: 8px;
}

.loading-animation {
    margin: 300px 0;
}

.stone-recommendations-empty-state {
    width: 100%;
    max-width: 800px;
    margin: 10vh auto;
    text-align: center;
}

.recommendations-wrapper {
    width: 1140px;
    max-width: 100%;
    margin: 0 auto;
    position: relative;

    .budget-slider {
        width: 840px;
        max-width: 100%;
        padding: 10px;
        margin-bottom: 24px;
        background-color: $stone-2;

        @include min-width(768) {
            margin: 0 auto;
            background-color: $white;

            display: flex;
            align-items: center;
        }

        .toggle-container {
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: space-between;

            @include min-width(768) {
                margin-top: 15px;
            }

            .label {
                margin-right: 40px;
                flex: 1 0 auto;
                font-size: 0.9rem;
                display: flex;
                align-content: center;
                justify-content: flex-start;
                white-space: nowrap;

                .icon {
                    margin-left: 5px;
                    font-size: 0.875rem;
                }

                .budget-range {
                    margin-left: auto;

                    @include min-width(768) {
                        display: none;
                    }
                }
            }

            .slider-toggle {
                color: $white;
                background-color: $blue;
                padding: 5px 15px;
                border-radius: 20px;
                cursor: pointer;
                user-select: none;

                font-family: $effra-font-stack;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                font-size: 0.8125rem;

                @include min-width(768) {
                    display: none;
                }
            }
        }

        .range-slider {
            display: none;
            width: 100%;
            padding: 16px 0;

            &.visible {
                display: block;
            }

            @include min-width(768) {
                display: block;
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 25px);

        &.disabled {
            display: none;
        }

        &.left {
            left: 15px;
        }

        &.right {
            right: 15px;
        }

        @include min-width(640) {
            display: none;
        }
    }
}

.recommendation-cards {
    --container-padding: 30px;
    position: relative;
    display: flex;
    overflow-y: hidden;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 var(--container-padding);
    padding: 0 var(--container-padding);
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
        display: none;
    }

    @include min-width(1024) {
        scroll-padding: 0;
    }

    @include fetching;

    @include min-width(640) {
        flex-wrap: nowrap;
    }

    .card {
        position: relative;
        user-select: none;
        scroll-snap-align: start;
        flex: 1 0 100%;
        padding: 0 10px;

        @include min-width(640) {
            margin: 0 5px;
            flex: 1 0 33.33%;

            &.mobile-extra {
                display: none;
            }
        }

        &.mobile-extra {
            .inner {
                width: 100%;
                height: 100%;
                padding: 24px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 36px;
                border: 1px solid $grey-200;

                background-color: $stone-2;

                .description {
                    text-align: center;
                    color: $blue;
                }
            }
        }
    }
}

.all-results-cta {
    width: 100%;
    display: flex;
    justify-content: center;
    margin: 30px 0 80px;
}
</style>
